// packages/api/src/query-keys.ts

/**
 * Centralized query key registry.
 * Every TanStack Query key is defined here to prevent typos
 * and ensure consistent invalidation.
 *
 * Naming convention:
 * - .all: matches ALL queries for this resource (for broad invalidation)
 * - .list(filters): list queries with optional filters
 * - .detail(id): single entity by ID
 */
export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
  },
  profile: {
    me: () => ['profile', 'me'] as const,
    tourConfig: () => ['profile', 'tour-config'] as const,
    myRegistration: () => ['profile', 'my-registration'] as const,
    addresses: () => ['profile', 'addresses'] as const,
    contacts: () => ['profile', 'contacts'] as const,
    bankAccounts: () => ['profile', 'bank-accounts'] as const,
    avatar: () => ['profile', 'avatar'] as const,
    organization: () => ['profile', 'organization'] as const,
    person: () => ['profile', 'person'] as const,
  },
  consultantSite: {
    all: ['consultant-site'] as const,
    me: () => ['consultant-site', 'me'] as const,
    slugAvailability: (slug: string) => ['consultant-site', 'slug-availability', slug] as const,
  },
  users: {
    all: ['users'] as const,
    list: (filters?: Record<string, unknown>) => ['users', 'list', filters] as const,
    detail: (id: string) => ['users', id] as const,
  },
  roles: {
    all: ['roles'] as const,
    list: (filters?: Record<string, unknown>) => ['roles', 'list', filters] as const,
    detail: (id: string) => ['roles', id] as const,
  },
  permissions: {
    all: ['permissions'] as const,
    list: ['permissions', 'list'] as const,
  },
  accounts: {
    all: ['accounts'] as const,
    list: (filters?: Record<string, unknown>) => ['accounts', 'list', filters] as const,
    detail: (id: string) => ['accounts', id] as const,
  },
  parties: {
    all: ['parties'] as const,
    list: (filters?: Record<string, unknown>) => ['parties', 'list', filters] as const,
    detail: (id: string) => ['parties', id] as const,
    byCpf: (cpf: string) => ['parties', 'by-cpf', cpf] as const,
    ancestors: (id: string) => ['parties', id, 'ancestors'] as const,
    descendants: (id: string) => ['parties', id, 'descendants'] as const,
    siblings: (id: string) => ['parties', id, 'siblings'] as const,
  },
  dashboard: {
    metrics: ['dashboard', 'metrics'] as const,
    stats: () => ['dashboard', 'stats'] as const,
    proposalOverview: (query?: { from?: string; to?: string }) =>
      ['dashboard', 'proposal-overview', query ?? {}] as const,
  },
  // Dashboard de gestão comercial (tela /metricas — ESCR-244).
  // Contratos: docs/contracts/ESCR-244.1.md + ESCR-244.2.md.
  metrics: {
    all: ['metrics'] as const,
    cards: (query?: Record<string, unknown>) => ['metrics', query ?? {}] as const,
    timeseries: (query?: Record<string, unknown>) =>
      ['metrics', 'timeseries', query ?? {}] as const,
    breakdown: (query?: Record<string, unknown>) => ['metrics', 'breakdown', query ?? {}] as const,
    consultants: (query?: Record<string, unknown>) =>
      ['metrics', 'consultants', query ?? {}] as const,
    // Meta comercial — classificações que contam como venda + vendas por
    // classificação. Contrato: docs/contracts/ESCR-meta-counted-operation-types.md.
    countedOperations: () => ['metrics', 'config', 'counted-operations'] as const,
    salesByOperation: (query?: Record<string, unknown>) =>
      ['metrics', 'sales-by-operation', query ?? {}] as const,
  },
  // Relatórios gerenciais (menu Relatórios — admin/gerente). Read-only org-wide.
  // Contrato: docs/contracts/ESCR-relatorios-gerenciais.md.
  reports: {
    all: ['reports'] as const,
    analystProductivity: (query?: Record<string, unknown>) =>
      ['reports', 'analyst-productivity', query ?? {}] as const,
    vehicleRanking: (query?: Record<string, unknown>) =>
      ['reports', 'vehicle-ranking', query ?? {}] as const,
    regionalConversion: (query?: Record<string, unknown>) =>
      ['reports', 'regional-conversion', query ?? {}] as const,
    monthlyClosing: (query?: Record<string, unknown>) =>
      ['reports', 'monthly-closing', query ?? {}] as const,
    // Onda 2 — R5 elasticidade de preço · R6 concentração · R7 retenção.
    priceElasticity: (query?: Record<string, unknown>) =>
      ['reports', 'price-elasticity', query ?? {}] as const,
    portfolioConcentration: (query?: Record<string, unknown>) =>
      ['reports', 'portfolio-concentration', query ?? {}] as const,
    retention: (query?: Record<string, unknown>) => ['reports', 'retention', query ?? {}] as const,
  },
  // Metas comerciais + relógio preditivo (tela /metricas — ESCR-245.3).
  // Contratos: docs/contracts/ESCR-245.1.md + ESCR-245.2.md.
  goals: {
    all: ['goals'] as const,
    list: (query?: Record<string, unknown>) => ['goals', query ?? {}] as const,
    clock: (query?: Record<string, unknown>) => ['goals', 'clock', query ?? {}] as const,
    // Tabela de gestão cross-month paginada (tela /metas — ESCR-255.1).
    // Contrato: docs/contracts/ESCR-255.1.md. GET /goals/list.
    managementList: (query?: Record<string, unknown>) =>
      ['goals', 'management-list', query ?? {}] as const,
  },
  // Meta de equipe (agregada, rede inteira) — supervisor/dono de time (ESCR-253).
  // Contrato: docs/contracts/ESCR-253.1.md. Owner-only (sempre o usuário logado).
  teamGoals: {
    all: ['team-goals'] as const,
    list: (period?: string) => ['team-goals', 'list', period ?? null] as const,
    progress: (query?: Record<string, unknown>) => ['team-goals', 'progress', query ?? {}] as const,
  },
  // Agenda / Lembretes do consultor (tela /agenda — ESCR-246.3).
  // Contratos: docs/contracts/ESCR-246.1.md + ESCR-246.2.md.
  reminders: {
    all: ['reminders'] as const,
    list: (params?: Record<string, unknown>) => ['reminders', 'list', params ?? {}] as const,
    searchTargets: (q: string) => ['reminders', 'search-targets', q] as const,
  },
  audit: {
    all: ['audit'] as const,
    list: (filters?: Record<string, unknown>) => ['audit', 'list', filters] as const,
  },
  privacy: {
    all: ['privacy'] as const,
    myData: ['privacy', 'my-data'] as const,
    consents: ['privacy', 'consents'] as const,
    adminDeletionRequests: {
      all: ['privacy', 'admin', 'deletion-requests'] as const,
      list: (filters?: Record<string, unknown>) =>
        ['privacy', 'admin', 'deletion-requests', 'list', filters] as const,
      detail: (id: string) => ['privacy', 'admin', 'deletion-requests', id] as const,
    },
  },
  registrations: {
    all: ['registrations'] as const,
    list: (filters?: Record<string, unknown>) => ['registrations', 'list', filters] as const,
    detail: (id: string) => ['registrations', id] as const,
    status: (trackingCode: string) => ['registrations', 'status', trackingCode] as const,
    sponsor: (referralCode: string) => ['registrations', 'sponsor', referralCode] as const,
    invite: (token: string) => ['registrations', 'invite', token] as const,
  },
  consultantPreRegistrations: {
    all: ['consultant-pre-registrations'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['consultant-pre-registrations', 'list', filters] as const,
    onboarding: (token: string) => ['consultant-pre-registrations', 'onboarding', token] as const,
  },
  consultantApproval: {
    all: ['consultant-approval'] as const,
    queue: (filters?: Record<string, unknown>) =>
      ['consultant-approval', 'queue', filters] as const,
    history: (filters?: Record<string, unknown>) =>
      ['consultant-approval', 'history', filters] as const,
    detail: (id: string) => ['consultant-approval', id] as const,
    stats: () => ['consultant-approval', 'stats'] as const,
  },
  // Ação manual de voluntário no SGA — mesma máquina de status nos 2 contextos
  // (approval + admin edit), sempre por consultant_profiles.id.
  sgaVoluntario: {
    status: (context: 'approval' | 'admin', consultantProfileId: string) =>
      ['sga-voluntario', context, consultantProfileId] as const,
  },
  // Recebedor de pagamento por provider (Iugu KYC) — mesmo endpoint admin nos 2
  // pontos, sempre por consultant_profiles.id. Contrato: ESCR-iugu-recipient-kyc.
  recipientKyc: {
    status: (consultantProfileId: string, provider: string) =>
      ['recipient-kyc', consultantProfileId, provider] as const,
  },
  firstAccess: {
    validate: (token: string) => ['first-access', 'validate', token] as const,
    legalTerms: () => ['first-access', 'legal-terms'] as const,
  },
  team: {
    all: ['team'] as const,
    members: (filters?: Record<string, unknown>) => ['team', 'members', filters] as const,
    kpis: () => ['team', 'kpis'] as const,
    referralLink: () => ['team', 'referral-link'] as const,
    referrals: (filters?: Record<string, unknown>) => ['team', 'referrals', filters] as const,
    tree: (maxDepth?: number, search?: string) => ['team', 'tree', maxDepth, search] as const,
    progression: (consultantId: string) => ['team', 'progression', consultantId] as const,
    myPath: () => ['team', 'my-path'] as const,
  },
  // Aniversariantes de CONSULTOR (admin_alamo + gerente) — GET
  // /admin/consultants/birthdays. Contrato: docs/contracts/ESCR-aniversariantes-consultores.md.
  consultantBirthdays: {
    all: ['consultant-birthdays'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['consultant-birthdays', 'list', filters ?? {}] as const,
  },
  // Aniversariantes de ASSOCIADO (visão do consultor — carteira SGA) — GET
  // /consultants/me/associates/birthdays. Contrato: docs/contracts/ESCR-aniversariantes-associados.md.
  associateBirthdays: {
    all: ['associate-birthdays'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['associate-birthdays', 'list', filters ?? {}] as const,
  },
  adminNetwork: {
    all: ['admin-network'] as const,
    dashboard: () => ['admin-network', 'dashboard'] as const,
    consultants: (filters?: Record<string, unknown>) =>
      ['admin-network', 'consultants', filters] as const,
    ranking: (filters?: Record<string, unknown>) => ['admin-network', 'ranking', filters] as const,
    distribution: () => ['admin-network', 'distribution'] as const,
    consultant: (id: string) => ['admin-network', 'consultant', id] as const,
    consultantTree: (id: string, maxDepth?: number, search?: string) =>
      ['admin-network', 'consultant', id, 'tree', maxDepth ?? null, search ?? null] as const,
    consultantPath: (id: string) => ['admin-network', 'consultant', id, 'path'] as const,
    teamDashboard: (id: string) => ['admin-network', 'consultant', id, 'team-dashboard'] as const,
    consultantAccess: (id: string) => ['admin-network', 'consultant', id, 'access'] as const,
    referrals: ['admin-network', 'referrals'] as const,
    // Inatividade de consultores (métrica read-only) — GET /admin/network/inactivity.
    // Contrato: docs/contracts/ESCR-consultant-inactivity-metrics.md.
    inactivity: (filters?: Record<string, unknown>) =>
      ['admin-network', 'inactivity', filters ?? {}] as const,
    // Detalhe de atividade de UM consultor — GET /admin/network/inactivity/:id.
    // Prefixo compartilhado com a listagem: `adminNetwork.all` invalida ambos.
    inactivityDetail: (id: string) => ['admin-network', 'inactivity', 'detail', id] as const,
  },
  locations: {
    all: ['locations'] as const,
    states: () => ['locations', 'states'] as const,
    citiesByUf: (uf: string, q?: string) => ['locations', 'cities', 'by-uf', uf, q ?? ''] as const,
    cityByIbge: (code: string) => ['locations', 'cities', 'by-ibge', code] as const,
    search: (q: string) => ['locations', 'cities', 'search', q] as const,
  },
  cep: {
    all: ['cep'] as const,
    lookup: (cep: string) => ['cep', cep] as const,
  },
  notificationTemplates: {
    all: ['notification-templates'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['notification-templates', 'list', filters] as const,
    detail: (id: string) => ['notification-templates', id] as const,
    variables: (filters?: Record<string, unknown>) =>
      ['notification-templates', 'variables', filters] as const,
  },
  communicationPreferences: {
    all: ['communication-preferences'] as const,
    public: (token: string) => ['communication-preferences', 'public', token] as const,
    admin: (partyId: string) => ['communication-preferences', 'admin', partyId] as const,
  },
  attachments: {
    all: ['attachments'] as const,
    list: (params?: Record<string, unknown>) => ['attachments', 'list', params] as const,
    detail: (id: string) => ['attachments', 'detail', id] as const,
    download: (id: string) => ['attachments', 'download', id] as const,
    quotationInvoice: (id: string) => ['attachments', 'quotation-invoice', id] as const,
  },
  // --- Phase 02.3.3 — Carta compliance pass hooks ---
  customers: {
    all: ['customers'] as const,
    lookup: (params: { cpf?: string | null; plate?: string | null }) =>
      ['customers', 'lookup', params] as const,
  },
  pricing: {
    all: ['pricing'] as const,
    optionals: (params: { planCode?: string; vehicleCategory?: string; subgroupId?: string }) =>
      ['pricing', 'optionals', params] as const,
    franchiseTiers: (params: {
      subgroupId?: string;
      planCode?: string;
      useType?: string;
      fipeValue?: number;
      regionId?: string;
      vehicleModelId?: string;
      isArmored?: boolean;
      hasGnv?: boolean;
      isRecuperado?: boolean;
      isElectric?: boolean;
    }) => ['pricing', 'franchise-tiers', params] as const,
    activationPaymentOptions: (params: { activationValue: number }) =>
      ['pricing', 'activation-payment-options', params] as const,
  },
  // Catálogo de motivos padronizados de recusa. `all` cobre list + detail +
  // options + contexts: editar o catálogo muda o seletor das telas de decisão,
  // então mutation invalida o prefixo inteiro.
  // Contrato: docs/contracts/ESCR-rejection-reasons.md.
  rejectionReasons: {
    all: ['admin', 'rejection-reasons'] as const,
    contexts: () => ['admin', 'rejection-reasons', 'contexts'] as const,
    options: (context: string) => ['admin', 'rejection-reasons', 'options', context] as const,
    list: (filters?: Record<string, unknown>) =>
      ['admin', 'rejection-reasons', 'list', filters ?? {}] as const,
    detail: (id: string) => ['admin', 'rejection-reasons', 'detail', id] as const,
  },
  // Central de Suporte / FAQ — consumo (consultor). Contrato:
  // docs/contracts/ESCR-faq-module.md. Permission `support:read`.
  faq: {
    all: ['faq'] as const,
    categories: () => ['faq', 'categories'] as const,
    grouped: () => ['faq', 'grouped'] as const,
    entries: (filters?: Record<string, unknown>) => ['faq', 'entries', filters ?? {}] as const,
  },
  // Central de Suporte / FAQ — admin CRUD (categorias + perguntas). Permission
  // `faq:manage`. Listagens paginam server-side { count, rows }.
  adminFaq: {
    all: ['admin-faq'] as const,
    categories: (filters?: Record<string, unknown>) =>
      ['admin-faq', 'categories', filters ?? {}] as const,
    category: (id: string) => ['admin-faq', 'category', id] as const,
    entries: (filters?: Record<string, unknown>) =>
      ['admin-faq', 'entries', filters ?? {}] as const,
    entry: (id: string) => ['admin-faq', 'entry', id] as const,
  },
  // Phase 05 — Policies & Contracts (POL-01..POL-04)
  policies: {
    all: ['policies'] as const,
    list: (filters?: Record<string, unknown>) => ['policies', 'list', filters] as const,
    detail: (id: string) => ['policies', id] as const,
    timeline: (id: string) => ['policies', id, 'timeline'] as const,
    // Endosso (tela /endossos) — rota legada aceita CPF ou placa.
    activeByCpf: (cpf: string) => ['policies', 'active-by-cpf', cpf] as const,
    activeByPlate: (plate: string) => ['policies', 'active-by-plate', plate] as const,
    endorsementContexts: (
      items: ReadonlyArray<{ key: string; plate: string }>,
      detectedVia?: 'manual_lookup' | 'cpf_lookup',
      customerCpf?: string,
    ) =>
      [
        'policies',
        'endorsement-contexts',
        detectedVia ?? 'manual_lookup',
        customerCpf ?? null,
        items,
      ] as const,
    legacyImportOptions: (
      quotationId: string,
      plate: string,
      vehicleVariantId?: string,
      planId?: string,
    ) =>
      [
        'policies',
        'legacy-import-options',
        quotationId,
        plate,
        vehicleVariantId ?? '',
        planId ?? '',
      ] as const,
    amendOptions: (id: string, policyItemId: string, planId?: string) =>
      ['policies', id, 'amend-options', policyItemId, planId ?? null] as const,
  },
  policyEndorsements: {
    all: ['policy-endorsements'] as const,
    admin: () => ['policy-endorsements', 'admin'] as const,
    adminList: (filters?: Record<string, unknown>) =>
      ['policy-endorsements', 'admin', 'list', filters ?? {}] as const,
    mine: () => ['policy-endorsements', 'mine'] as const,
    mineList: (filters?: Record<string, unknown>) =>
      ['policy-endorsements', 'mine', 'list', filters ?? {}] as const,
    detail: (id: string) => ['policy-endorsements', 'detail', id] as const,
    financialOrigin: (id: string) =>
      ['policy-endorsements', 'detail', id, 'financial-origin'] as const,
    overrides: (id: string) => ['policy-endorsements', 'detail', id, 'overrides'] as const,
    adminOpportunities: (filters?: Record<string, unknown>) =>
      ['policy-endorsements', 'admin', 'opportunities', filters ?? {}] as const,
    myOpportunities: (filters?: Record<string, unknown>) =>
      ['policy-endorsements', 'mine', 'opportunities', filters ?? {}] as const,
    plateSuggestions: (normalizedPrefix: string, limit?: number) =>
      ['policy-endorsements', 'plate-suggestions', normalizedPrefix, limit ?? null] as const,
  },
  legacyEndorsementMappings: {
    all: ['legacy-endorsement-mappings'] as const,
    list: (filters?: unknown) => ['legacy-endorsement-mappings', 'list', filters ?? {}] as const,
  },
  // Phase 12 analista-queue-kpis-notif Plan 16 — Admin Hinova sync hooks.
  // Namespace separado de `policies` (Phase 05 lifecycle) para evitar conflito
  // D-34 — domínio admin/sync vs consultor lifecycle. Coexistência (Opção B).
  policiesAdmin: {
    all: ['policies-admin'] as const,
    list: (filters: unknown) => ['policies-admin', 'list', filters] as const,
    detail: (id: string) => ['policies-admin', 'detail', id] as const,
    syncStatus: (id: string) => ['policies-admin', 'sync-status', id] as const,
  },
  // Phase 09 pipeline-foundation — Proposals (Plan 09 hooks)
  // Contract refs: docs/contracts/ESCR-pipeline-foundation-06.md
  // Key shapes mirror @alamo/api convention (`resource.all`, `.list(filters)`,
  // `.detail(id)`, nested resources keyed under detail).
  proposals: {
    all: ['proposals'] as const,
    list: (filters?: Record<string, unknown>) => ['proposals', 'list', filters ?? {}] as const,
    detail: (id: string) => ['proposals', 'detail', id] as const,
    commercialEditContext: (id: string) => ['proposals', id, 'commercial-edit-context'] as const,
    commercialRevisions: (id: string) => ['proposals', id, 'commercial-revisions'] as const,
    reopeningPreview: (id: string) => ['proposals', id, 'reopening-preview'] as const,
    vehicleModelCorrectionPreview: (id: string) =>
      ['proposals', id, 'vehicle-model-correction-preview'] as const,
    // Bloco 02 — leitura agregada da tela "Dados" (pessoa + endereços + contatos + veículo).
    data: (id: string) => ['proposals', 'data', id] as const,
    // Bloco 02 — sugestão de prefill da base interna (on-demand, ao abrir o modal).
    prefillSuggestion: (id: string) => ['proposals', 'prefill-suggestion', id] as const,
    timeline: (id: string, filters?: Record<string, unknown>) =>
      ['proposals', id, 'timeline', filters ?? {}] as const,
    attentionFlags: (id: string) => ['proposals', id, 'attention-flags'] as const,
    publicByToken: (token: string) => ['proposals', 'public', token] as const,
    magicLinkUrl: (id: string) => ['proposals', id, 'magic-link-url'] as const,
    // Contrato assinado (download do consultor) — GET /proposals/:id/signature/signed-contract.
    // Vive no namespace `proposals` porque a chave é por PROPOSALID; o namespace
    // `signature.*` abaixo é token-based (magic link) e não deve ser misturado.
    // Contrato: docs/contracts/ESCR-consultor-laudo-contrato.md.
    signedContract: (id: string) => ['proposals', 'signed-contract', id] as const,
    // ESCR-247.3 — Pipeline por etapa + contador de paradas (+7d).
    pipeline: (filters?: Record<string, unknown>) =>
      ['proposals', 'pipeline', filters ?? {}] as const,
    monitoring: (filters?: Record<string, unknown>) =>
      ['proposals', 'monitoring', filters ?? {}] as const,
    stalledCount: (filters?: Record<string, unknown>) =>
      ['proposals', 'stalled-count', filters ?? {}] as const,
  },
  // Quotation-side timeline (proposal_status_history rows com quotation_id).
  // Espelha `proposals.timeline` — GET /quotations/:id/timeline.
  quotations: {
    all: ['quotations'] as const,
    timeline: (id: string, filters?: Record<string, unknown>) =>
      ['quotations', id, 'timeline', filters ?? {}] as const,
    // Histórico de cálculo (memória por snapshot) — GET /quotations/:id/breakdown-history.
    breakdownHistory: (id: string, filters?: Record<string, unknown>) =>
      ['quotations', id, 'breakdown-history', filters ?? {}] as const,
    // Auditoria field-level das edições — GET /quotations/:id/audit (escopado ao dono).
    audit: (id: string) => ['quotations', 'audit', id] as const,
    // Aviso de proteção já existente na placa — GET /quotations/:id/plate-advisory.
    plateAdvisory: (id: string) => ['quotations', id, 'plate-advisory'] as const,
    inputPlateAdvisory: (plate: string) => ['quotations', 'plate-input-advisory', plate] as const,
  },
  // Phase 18 Plan 09 (REQ-14) — magic-link cliente pendencias filtered view.
  // Contract: docs/contracts/ESCR-pendencias-public-magic-link.md.
  // 2 endpoints sob /p/:token/pendencias usando PublicWriteTokenGuard.
  pendencias: {
    all: ['pendencias'] as const,
    publicByToken: (token: string) => ['pendencias', 'public', token] as const,
  },
  // Phase 10 vistoria-module — Inspections (Plan 06 hooks)
  // Contract refs: docs/contracts/ESCR-vistoria-module-04.md
  // Endpoints:
  //   - GET  /proposals/:id/inspection                       → detail (status sem PII)
  //   - GET  /p/:token/inspection                            → public status (polling)
  //   - POST /p/:token/inspection/start                      → cria + uploadToken
  //   - POST /p/:token/inspection/consent                    → LGPD consent
  //   - POST /p/:uploadToken/inspection/photos/:slotKey      → upload foto slot
  //   - POST /p/:uploadToken/inspection/selfie               → upload selfie
  //   - POST /p/:uploadToken/inspection/submit               → finalize + enqueue
  //   - GET  /proposals/:id/inspection/report.pdf            → laudo PDF
  //   - GET  /admin/inspection-types                         → admin paginated list
  //   - POST/PATCH/DELETE /admin/inspection-types(/:id)      → admin CRUD
  inspection: {
    all: ['inspection'] as const,
    detail: (proposalId: string) => ['inspection', 'detail', proposalId] as const,
    photos: (proposalId: string, page?: number, limit?: number) =>
      ['inspection', 'photos', proposalId, page ?? 1, limit ?? 20] as const,
    // Prefixo (sem page/limit) para invalidar todas as páginas de uma proposta.
    photosAll: (proposalId: string) => ['inspection', 'photos', proposalId] as const,
    status: (token: string) => ['inspection', 'status', token] as const,
    report: (proposalId: string) => ['inspection', 'report', proposalId] as const,
    types: {
      all: ['inspection', 'types'] as const,
      list: (filters?: Record<string, unknown>) =>
        ['inspection', 'types', 'list', filters ?? {}] as const,
      detail: (id: string) => ['inspection', 'types', 'detail', id] as const,
    },
  },
  // Phase 11 docs-signature-payment — Plan 09 hooks (proposal-documents,
  // signatures, payments, admin CRUD document_types/contract_templates/webhooks).
  // Contract refs:
  //   - docs/contracts/ESCR-docs-signature-payment-02.md (Clicksign)
  //   - docs/contracts/ESCR-docs-signature-payment-03.md (Asaas + wallet dual-write)
  //   - docs/contracts/ESCR-docs-signature-payment-04.md (Documents + PublicWriteToken D-50)
  proposalDocuments: {
    all: ['proposal-documents'] as const,
    list: (proposalId: string, query?: Record<string, unknown>) =>
      ['proposal-documents', proposalId, query ?? {}] as const,
    slots: (proposalId: string) => ['proposal-documents', proposalId, 'slots'] as const,
    // Prefixo por proposta (sem filtros) — invalida todas as listas dessa proposta.
    byProposal: (proposalId: string) => ['proposal-documents', proposalId] as const,
  },
  signature: {
    all: ['signature'] as const,
    status: (token: string) => ['signature', 'status', token] as const,
    dueDayOptions: (token: string) => ['signature', 'due-day-options', token] as const,
  },
  contract: {
    all: ['contract'] as const,
    preview: (token: string) => ['contract', 'preview', token] as const,
  },
  payment: {
    all: ['payment'] as const,
    status: (token: string) => ['payment', 'status', token] as const,
    proposalStatus: (proposalId: string) => ['payment', 'proposal', proposalId, 'status'] as const,
    // Autocotação — cobrança da taxa de adesão ancorada NA COTAÇÃO (a proposta só
    // nasce ao confirmar o pagamento). Anchor = quotationId (inadivinhável).
    quotationStatus: (quotationId: string) =>
      ['payment', 'quotation', quotationId, 'status'] as const,
    // Card-options da autocotação (gateway/iuguAccountId/iuguTestMode) — liga o
    // cartão TRANSPARENTE Iugu. Anchor = quotationId (sem token proposal).
    quotationCardOptions: (quotationId: string) =>
      ['payment', 'quotation', quotationId, 'card-options'] as const,
  },
  // Admin payment gateway selector (Asaas | Iugu).
  // Contract: docs/contracts/ESCR-iugu-gateway.md.
  paymentGateway: {
    all: ['payment-gateway'] as const,
    status: () => ['payment-gateway', 'status'] as const,
  },
  // Repasse manual da adesão (Iugu único / split gated) — fila admin.
  // Contract: docs/contracts/ESCR-pending-payouts.md.
  pendingPayouts: {
    all: ['pending-payouts'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['pending-payouts', 'list', filters ?? {}] as const,
    detail: (id: string) => ['pending-payouts', 'detail', id] as const,
  },
  paymentIncidents: {
    all: ['payment-incidents'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['payment-incidents', 'list', filters ?? {}] as const,
    detail: (id: string) => ['payment-incidents', 'detail', id] as const,
    openCount: () => ['payment-incidents', 'open-count'] as const,
  },
  reactivationBilling: {
    all: ['reactivation-billing'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['reactivation-billing', 'list', filters ?? {}] as const,
    openCount: () => ['reactivation-billing', 'open-count'] as const,
    detail: (id: string) => ['reactivation-billing', 'detail', id] as const,
  },
  reactivationReport: {
    all: ['reactivation-report'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['reactivation-report', 'list', filters ?? {}] as const,
    summary: (filters?: Record<string, unknown>) =>
      ['reactivation-report', 'summary', filters ?? {}] as const,
    requesters: (scope?: Record<string, unknown>) =>
      ['reactivation-report', 'requesters', scope ?? {}] as const,
  },
  paymentOnboardingOperations: {
    all: ['payment-onboarding-operations'] as const,
    jobs: (filters?: Record<string, unknown>) =>
      ['payment-onboarding-operations', 'jobs', filters ?? {}] as const,
    metrics: () => ['payment-onboarding-operations', 'metrics'] as const,
    consultant: (consultantProfileId: string) =>
      ['payment-onboarding-operations', 'consultant', consultantProfileId] as const,
  },
  activationPaymentsReport: {
    all: ['activation-payments-report'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['activation-payments-report', 'list', filters ?? {}] as const,
    summary: (filters?: Record<string, unknown>) =>
      ['activation-payments-report', 'summary', filters ?? {}] as const,
  },
  activationFeeReport: {
    all: ['activation-fee-report'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['activation-fee-report', 'list', filters ?? {}] as const,
    summary: (filters?: Record<string, unknown>) =>
      ['activation-fee-report', 'summary', filters ?? {}] as const,
  },
  documentTypes: {
    all: ['document-types'] as const,
    list: (filters?: Record<string, unknown>) => ['document-types', 'list', filters ?? {}] as const,
    detail: (id: string) => ['document-types', 'detail', id] as const,
  },
  // Comissão MLM Genial
  commissionTables: {
    all: ['commission-tables'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['commission-tables', 'list', filters ?? {}] as const,
    detail: (id: string) => ['commission-tables', 'detail', id] as const,
    history: (id: string) => ['commission-tables', 'history', id] as const,
  },
  commissionTiers: {
    all: ['commission-tiers'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['commission-tiers', 'list', filters ?? {}] as const,
    detail: (id: string) => ['commission-tiers', 'detail', id] as const,
    history: (id: string) => ['commission-tiers', 'history', id] as const,
  },
  commissionRuns: {
    all: ['commission-runs'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['commission-runs', 'list', filters ?? {}] as const,
    detail: (id: string) => ['commission-runs', 'detail', id] as const,
    details: (id: string, filters?: Record<string, unknown>) =>
      ['commission-runs', 'details', id, filters ?? {}] as const,
    active: () => ['commission-runs', 'active'] as const,
  },
  commissionBalances: {
    all: ['commission-balances'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['commission-balances', 'list', filters ?? {}] as const,
    detail: (id: string) => ['commission-balances', 'detail', id] as const,
    history: (id: string) => ['commission-balances', 'history', id] as const,
  },
  // Fio de mensagens do saldo (contestação + pendência) — compartilhado
  // self/admin. Contrato: docs/contracts/ESCR-commission-dispute-pendency.md.
  commissionBalanceMessages: {
    all: (balanceId: string) => ['commission-balance', 'messages', balanceId] as const,
    list: (balanceId: string, page?: number) =>
      ['commission-balance', 'messages', balanceId, page ?? 1] as const,
  },
  // Contador de atenção do financeiro (badge do menu admin de comissão).
  // Contrato: docs/contracts/ESCR-commission-attention-count-and-paid-guard.md.
  commissionAttention: {
    count: () => ['commissions', 'attention-count'] as const,
  },
  commissionStatement: {
    detail: (consultantProfileId: string, competence: string) =>
      ['commission-statement', consultantProfileId, competence] as const,
    entries: (consultantProfileId: string, params: Record<string, unknown>) =>
      ['commissions', 'statement-entries', consultantProfileId, params] as const,
  },
  // Relatório de comissionamento (preview JSON dos 8 blocos) — Phase 38
  commissionReport: {
    consultant: (consultantProfileId: string, competence: string) =>
      ['commission-report', 'consultant', consultantProfileId, competence] as const,
    me: (competence: string) => ['commission-report', 'me', competence] as const,
  },
  // Ajustes manuais de comissão (crédito/débito) — ESCR-comissao-ajustes-manuais
  commissionAdjustments: {
    all: ['commission-adjustments'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['commission-adjustments', 'list', filters ?? {}] as const,
    detail: (id: string) => ['commission-adjustments', 'detail', id] as const,
  },
  // Elegibilidade de comissão por consultor (Phase 32)
  consultantEligibility: {
    all: ['consultant-eligibility'] as const,
    detail: (consultantProfileId: string) =>
      ['consultant-eligibility', 'detail', consultantProfileId] as const,
  },
  myCommissions: {
    all: ['my-commissions'] as const,
    balances: (filters?: Record<string, unknown>) =>
      ['my-commissions', 'balances', filters ?? {}] as const,
    balanceDetail: (id: string) => ['my-commissions', 'balance-detail', id] as const,
    statement: (competence?: string) =>
      ['my-commissions', 'statement', competence ?? 'current'] as const,
    statementEntries: (params: Record<string, unknown>) =>
      ['commissions', 'me', 'statement-entries', params] as const,
    preview: (competence?: string) =>
      ['my-commissions', 'preview', competence ?? 'current'] as const,
    report: (competence: string) => ['commission-report', 'me', competence] as const,
  },
  contractTemplates: {
    all: ['contract-templates'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['contract-templates', 'list', filters ?? {}] as const,
    detail: (id: string) => ['contract-templates', 'detail', id] as const,
    coverage: () => ['contract-templates', 'coverage'] as const,
  },
  webhookEvents: {
    all: ['webhook-events'] as const,
    list: (filters?: Record<string, unknown>) => ['webhook-events', 'list', filters ?? {}] as const,
    detail: (id: string) => ['webhook-events', 'detail', id] as const,
    // Providers reais presentes na tabela (alimenta o filtro dinâmico).
    // Contrato: docs/contracts/ESCR-webhooks-provider-filter-dynamic.md.
    providers: () => ['webhook-events', 'providers'] as const,
  },
  // Phase 12 analista-queue-kpis-notif Plan 11 — 7 factories.
  // Contract: docs/contracts/ESCR-analista-queue-kpis-notif-05.md.
  analystQueue: {
    all: ['analyst-queue'] as const,
    list: (filters?: Record<string, unknown>) => ['analyst-queue', 'list', filters ?? {}] as const,
    kanbanColumn: (filters?: Record<string, unknown>) =>
      ['analyst-queue', 'kanban-column', filters ?? {}] as const,
  },
  // Histórico de análises (propostas já decididas) — GET
  // /admin/proposals/analysis-history. Lazy (query enabled só com busca/período).
  // Contrato: docs/contracts/ESCR-analista-fila-decididas-historico.md.
  analystHistory: {
    all: ['analyst-history'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['analyst-history', 'list', filters ?? {}] as const,
  },
  proposalAnalysis: {
    all: ['proposal-analysis'] as const,
    detail: (id: string) => ['proposal-analysis', 'detail', id] as const,
    auditTimeline: (id: string, filters?: Record<string, unknown>) =>
      ['proposal-analysis', 'audit-timeline', id, filters ?? {}] as const,
    /** Phase 18 Plan 10 (REQ-17) — previous reviews por proposta. */
    previousReviews: (id: string) => ['proposal-analysis', 'previous-reviews', id] as const,
    /** Phase 18.2 — PII access summary (LGPD audit visibility) por proposta. */
    piiAccessSummary: (id: string) => ['proposal-analysis', 'pii-access-summary', id] as const,
    /** Phase 18.3 conserto — anotacoes internas por proposta (substitui localStorage). */
    internalNotes: (id: string) => ['proposal-analysis', 'internal-notes', id] as const,
    sharedMessages: (id: string) => ['proposal-analysis', 'shared-messages', id] as const,
    /**
     * Consulta AO VIVO da placa da proposta no SGA (ponto de atenção read-only).
     * Contract: docs/contracts/ESCR-sga-vehicle-check-analyst.md.
     */
    sgaVehicleCheck: (id: string) => ['proposal-analysis', 'sga-vehicle-check', id] as const,
    /**
     * Painel informativo de precificação (cotação sem-FIPE com modelo vinculado
     * depois). Contract: docs/contracts/ESCR-painel-alertas-analista.md.
     */
    pricingReview: (id: string) => ['proposal-analysis', 'pricing-review', id] as const,
  },
  proposalMessages: {
    all: ['proposal-messages'] as const,
    detail: (id: string) => ['proposal-messages', id] as const,
  },
  // Transmissão FORWARD do contrato ao Hinova SGA (ação do analista pós-aprovação).
  // Contract: docs/contracts/ESCR-sga-forward-transmission.md.
  sgaIntegration: {
    all: ['sga-integration'] as const,
    status: (proposalId: string) => ['sga-integration', 'status', proposalId] as const,
    history: (proposalId: string) => ['sga-integration', 'history', proposalId] as const,
  },
  kpis: {
    all: ['kpis'] as const,
    list: (filters?: Record<string, unknown>) => ['kpis', 'list', filters ?? {}] as const,
    trend: (kpiKey: string, analystUserId?: string) =>
      ['kpis', 'trend', kpiKey, analystUserId ?? null] as const,
  },
  warRoom: {
    all: ['war-room'] as const,
    snapshot: () => ['war-room', 'snapshot'] as const,
  },
  notificationSettings: {
    all: ['notification-settings'] as const,
    catalog: () => ['notification-settings', 'catalog'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['notification-settings', 'list', filters ?? {}] as const,
  },
  notificationCadence: {
    all: ['notification-cadence'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['notification-cadence', 'list', filters ?? {}] as const,
  },
  notificationOperations: {
    all: ['notification-operations'] as const,
    overview: () => ['notification-operations', 'overview'] as const,
    providers: () => ['notification-operations', 'providers'] as const,
    deliveries: (filters?: Record<string, unknown>) =>
      ['notification-operations', 'deliveries', filters ?? {}] as const,
    delivery: (id: string) => ['notification-operations', 'delivery', id] as const,
    redrives: (filters?: Record<string, unknown>) =>
      ['notification-operations', 'redrives', filters ?? {}] as const,
  },
  integrationWebhooks: {
    all: ['integration-webhooks'] as const,
    subscriptions: (filters?: Record<string, unknown>) =>
      ['integration-webhooks', 'subscriptions', filters ?? {}] as const,
    deliveries: (filters?: Record<string, unknown>) =>
      ['integration-webhooks', 'deliveries', filters ?? {}] as const,
    delivery: (id: string) => ['integration-webhooks', 'delivery', id] as const,
  },
  marketingRd: {
    all: ['admin-marketing'] as const,
    diagnostics: () => ['admin-marketing', 'diagnostics'] as const,
    triggers: () => ['admin-marketing', 'triggers'] as const,
    presetLists: () => ['admin-marketing', 'presets'] as const,
    presets: (filters?: Record<string, unknown>) =>
      ['admin-marketing', 'presets', filters ?? {}] as const,
    automationLists: () => ['admin-marketing', 'automations'] as const,
    automations: (filters?: Record<string, unknown>) =>
      ['admin-marketing', 'automations', filters ?? {}] as const,
    activationLists: () => ['admin-marketing', 'activations'] as const,
    activations: (filters?: Record<string, unknown>) =>
      ['admin-marketing', 'activations', filters ?? {}] as const,
    activation: (id: string) => ['admin-marketing', 'activation', id] as const,
    analytics: (filters?: Record<string, unknown>) =>
      ['admin-marketing', 'analytics', filters ?? {}] as const,
  },
  attentionFlags: {
    all: ['attention-flags'] as const,
    detail: (proposalId: string) => ['attention-flags', 'detail', proposalId] as const,
  },
  // Phase A (2026-05-22) — Time de analise.
  analystTeam: {
    all: ['analyst-team'] as const,
    list: (filters?: Record<string, unknown>) => ['analyst-team', 'list', filters ?? {}] as const,
    candidateUsers: (q: string) => ['analyst-team', 'candidate-users', q] as const,
    detail: (userId: string) => ['analyst-team', 'detail', userId] as const,
    options: () => ['analyst-team', 'options'] as const,
    supervisorOptions: () => ['analyst-team', 'supervisor-options'] as const,
    me: () => ['analyst-team', 'me'] as const,
  },
  // Phase 13 (ESCR-172.5) — Alamo Cobre (cobertura de proposta concorrente).
  competitorOffers: {
    all: ['competitor-offers'] as const,
    config: () => ['competitor-offers', 'config'] as const,
    byQuotation: (quotationId: string) =>
      ['competitor-offers', 'by-quotation', quotationId] as const,
    byFleet: (fleetQuotationId: string) =>
      ['competitor-offers', 'by-fleet', fleetQuotationId] as const,
    detail: (requestId: string) => ['competitor-offers', 'detail', requestId] as const,
    my: (filters?: Record<string, unknown>) => ['competitor-offers', 'my', filters ?? {}] as const,
  },
  // ESCR-172.6 — Alamo Cobre UI analista (fila + detalhe + decisão).
  adminCompetitorOffers: {
    all: ['admin-competitor-offers'] as const,
    queue: (filters?: Record<string, unknown>) =>
      ['admin-competitor-offers', 'queue', filters ?? {}] as const,
    queueCount: (status: string) => ['admin-competitor-offers', 'queue-count', status] as const,
    detail: (id: string) => ['admin-competitor-offers', 'detail', id] as const,
  },
  // Phase 34 — Loja Interna (catálogo vitrine consultor)
  storeProducts: {
    all: ['store-products'] as const,
    list: (filters?: Record<string, unknown>) => ['store-products', 'list', filters ?? {}] as const,
    detail: (id: string) => ['store-products', 'detail', id] as const,
  },
  // Phase 34 CR-11 — Configuração de frete (staleTime infinito — config raramente muda)
  storeShippingConfig: ['store-shipping-config'] as const,
  // Phase 34 — Loja Interna (pedidos consultor — JWT-scoped)
  storeOrders: {
    all: ['store-orders'] as const,
    myList: (page?: number) => ['store-orders', 'my-list', page ?? 1] as const,
    myDetail: (id: string) => ['store-orders', 'my-detail', id] as const,
    // ESCR-249 — estado da cobrança do pedido (polling PIX / leitura cartão).
    payment: (id: string) => ['store-orders', 'payment', id] as const,
  },
  // Phase 34 — Loja Interna Admin (produtos)
  adminStoreProducts: {
    all: ['admin-store-products'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['admin-store-products', 'list', filters ?? {}] as const,
    detail: (id: string) => ['admin-store-products', 'detail', id] as const,
  },
  // Phase 34 — Loja Interna Admin (pedidos)
  adminStoreOrders: {
    all: ['admin-store-orders'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['admin-store-orders', 'list', filters ?? {}] as const,
    detail: (id: string) => ['admin-store-orders', 'detail', id] as const,
    history: (id: string) => ['admin-store-orders', 'history', id] as const,
  },
  // Phase 34 — Loja Interna Admin (estoque)
  adminStoreStock: {
    all: ['admin-store-stock'] as const,
    movements: (filters?: Record<string, unknown>) =>
      ['admin-store-stock', 'movements', filters ?? {}] as const,
    balances: (filters?: Record<string, unknown>) =>
      ['admin-store-stock', 'balances', filters ?? {}] as const,
  },
  // Phase 34 — Loja Interna categorias (admin CRUD) + filtros vitrine
  adminStoreCategories: {
    all: ['admin-store-categories'] as const,
    list: () => ['admin-store-categories', 'list'] as const,
  },
  storeFilters: {
    all: ['store-filters'] as const,
    result: () => ['store-filters', 'result'] as const,
  },
  // ESCR-store-vouchers — vouchers de desconto da loja (admin CRUD).
  // Contrato: docs/contracts/ESCR-store-vouchers.md.
  storeVouchers: {
    all: ['store-vouchers'] as const,
    list: (filters?: Record<string, unknown>) => ['store-vouchers', 'list', filters ?? {}] as const,
    redemptions: (id: string, filters?: Record<string, unknown>) =>
      ['store-vouchers', 'redemptions', id, filters ?? {}] as const,
  },
  // ESCR-250 — Crédito da loja (config admin + faixas + disponível do consultor)
  storeCredit: {
    me: ['store-credit', 'me'] as const,
  },
  adminStoreCredit: {
    all: ['admin-store-credit'] as const,
    config: ['store-credit-config'] as const,
    tiers: (filters?: Record<string, unknown>) =>
      ['store-credit-tiers', 'list', filters ?? {}] as const,
    // ESCR-251.7 — acompanhamento de parcelas (compra → parcela → comissão).
    installments: (filters?: Record<string, unknown>) =>
      ['store-credit-installments', 'list', filters ?? {}] as const,
    orderInstallments: (orderId: string) =>
      ['store-credit-installments', 'order', orderId] as const,
    // ESCR-251.8 — relatório financeiro do crédito (contratado/cobrado/a-receber).
    financialReport: (filters?: Record<string, unknown>) =>
      ['store-credit-financial-report', 'list', filters ?? {}] as const,
    financialProjection: (consultantId?: string) =>
      ['store-credit-financial-report', 'projection', consultantId ?? null] as const,
  },
} as const;

/**
 * Query keys for the Pricing Admin redesign (W3.2 of
 * `cara-a-primeira-coisa-greedy-marshmallow.md`). Namespaced under
 * `admin/pricing` to keep them separate from the legacy
 * `admin/pricing-tables` keys exposed by `usePricingTables.ts`, which
 * stay until W5 cleanup.
 */
export const pricingAdminKeys = {
  all: ['admin', 'pricing'] as const,
  stats: () => [...pricingAdminKeys.all, 'stats'] as const,
  tables: () => [...pricingAdminKeys.all, 'tables'] as const,
  table: (id: string) => [...pricingAdminKeys.tables(), id] as const,
  tableHistory: (id: string) => [...pricingAdminKeys.table(id), 'history'] as const,
  tableDiff: (id: string) => [...pricingAdminKeys.table(id), 'diff'] as const,
  adjustments: () => [...pricingAdminKeys.all, 'adjustments'] as const,
  adjustment: (id: string) => [...pricingAdminKeys.adjustments(), id] as const,
  adjustmentHistory: (id: string) => [...pricingAdminKeys.adjustment(id), 'history'] as const,
  // zones/cep-zones movidos para `geographyKeys` (refactor 2026-04-30)
  deductibles: (filters?: Record<string, unknown>) =>
    [...pricingAdminKeys.all, 'deductibles', filters] as const,
  deductible: (id: string) => [...pricingAdminKeys.all, 'deductibles', id] as const,
  // Migration 0129/0131 (2026-05-21) — deductible_tier_adjustments fundido
  // como deltas inline em `deductibles`. Query keys dropados.
  // ESCR-200.13 — 3 templates editaveis (reduzida/basica/ampliada). Header
  // cards de FranchiseSection consomem.
  deductibleLevelsConfig: () => [...pricingAdminKeys.all, 'deductible-levels-config'] as const,
  // ESCR-199.2 — singleton (GET /admin/activation-fee). Workflow histórico
  // por id mantido via `activationFeeById`.
  activationFee: () => [...pricingAdminKeys.all, 'activation-fee', 'singleton'] as const,
  activationFeeById: (id: string) =>
    [...pricingAdminKeys.all, 'activation-fee', 'by-id', id] as const,
  acceptanceRules: (filters?: Record<string, unknown>) =>
    [...pricingAdminKeys.all, 'acceptance-rules', filters] as const,
  acceptanceRule: (id: string) => [...pricingAdminKeys.all, 'acceptance-rules', id] as const,
  history: (entityType: string, id: string) =>
    [...pricingAdminKeys.all, 'history', entityType, id] as const,
} as const;

/**
 * Phase 08 Plan 05 (ESCR-216) — entity-level linear history.
 * Consumed by useEntityHistory hook + EntityHistoryDrawer component.
 */
export const pricingHistoryKeys = {
  all: ['pricing', 'history'] as const,
  entity: (entityType: string, entityId: string) =>
    [...pricingHistoryKeys.all, entityType, entityId] as const,
} as const;

/**
 * Phase 08 Plan 06 (ESCR-218) — Change Set list + detail.
 * Consumed by useChangeSetsList, useChangeSetDetail, and all CS mutations (broad invalidation).
 */
export const pricingChangeSetKeys = {
  all: ['pricing', 'change-sets'] as const,
  list: (filters: Record<string, unknown>) =>
    [...pricingChangeSetKeys.all, 'list', filters] as const,
  detail: (id: string) => [...pricingChangeSetKeys.all, 'detail', id] as const,
};

/**
 * Phase 08 Plan 06 (ESCR-218) — Notificações filtradas de Change Set.
 * Consumed by useChangeSetNotifications (polling 30s).
 * NÃO invalidado por mutations CS (evita loop T-08-06-NOTIFY-LOOP).
 */
export const pricingChangeSetNotificationKeys = {
  all: ['notifications', 'change-set'] as const,
};

/**
 * Inbox do usuário autenticado (sininho / topbar).
 * Contrato: docs/contracts/ESCR-public-quote-consultor-notify.md.
 * Consome GET /notifications (+ /unread-count). Invalidado por mark-read,
 * mark-all-read e pelo evento Socket.IO `notifications.created`.
 */
export const notificationsInboxKeys = {
  all: ['notifications', 'inbox'] as const,
  list: (filters: Record<string, unknown>) => ['notifications', 'inbox', 'list', filters] as const,
  unreadCount: () => ['notifications', 'inbox', 'unread-count'] as const,
};

/**
 * Web Push (browser push transport).
 * Contrato: docs/contracts/ESCR-web-push-transport.md.
 * Consome GET /notifications/vapid-public-key. Mutations (POST/DELETE
 * subscriptions) não têm cache próprio.
 */
export const webPushKeys = {
  all: ['notifications', 'web-push'] as const,
  vapidPublicKey: () => ['notifications', 'web-push', 'vapid-public-key'] as const,
};

/**
 * Geography refactor 2026-04-30 — query keys do novo módulo /admin/geography.
 */
export const geographyKeys = {
  all: ['geography'] as const,
  tree: (opts?: { parentId?: string; counts?: boolean }) =>
    ['geography', 'tree', opts ?? {}] as const,
  resolve: (input: { cep?: string; lat?: number; lng?: number }) =>
    ['geography', 'resolve', input] as const,
  // GET /admin/geography/zones/:id → { zone, chain[] }
  zoneWithChain: (id: string) => ['geography', 'zone-with-chain', id] as const,
  // GET /admin/geography/zones/:id/chain → { zone, chain[], adjustments[], finalFactor }
  zoneChain: (id: string) => ['geography', 'zone-chain', id] as const,
  // GET /admin/geography/zones/search?q= → GeographyTreeNode[] (combobox buscável)
  zoneSearch: (q: string, limit: number) => ['geography', 'zone-search', q, limit] as const,
  riskOverlays: (filters?: Record<string, unknown>) =>
    ['geography', 'risk-overlays', filters ?? {}] as const,
  riskOverlay: (id: string) => ['geography', 'risk-overlay', id] as const,
  overlayRules: (overlayId: string) =>
    ['geography', 'risk-overlay', overlayId, 'acceptance-rules'] as const,
  // XXX3.10 — métricas agregadas por regra de aceitação (rejections count +
  // value_adjusted_sum em janela 7d/30d/90d). Endpoint:
  // GET /admin/geography/risk-overlays/:id/acceptance-rules/stats?period=…
  acceptanceRuleStats: (overlayId: string, period: '7d' | '30d' | '90d') =>
    ['geography', 'risk-overlay', overlayId, 'acceptance-rules', 'stats', period] as const,
  // XXX3.17 — timeline unificada (UNION audit_logs + approval_history) por zona.
  // Endpoint: GET /admin/geography/zones/:id/history?page=&limit=
  zoneHistory: (zoneId: string, params?: { page?: number; limit?: number }) =>
    ['geography', 'zone', zoneId, 'history', params ?? {}] as const,
  // Contrato ESCR-geo-panel-adjustments — agrupamentos que contêm cidade/ponto.
  // GET /admin/geography/containing-groupings?cityId=|lat=&lng=
  containingGroupings: (input: { cityId?: string; lat?: number; lng?: number }) =>
    ['geography', 'containing-groupings', input] as const,
} as const;

// zonesKeys (legacy /admin/zones) removido no refactor 2026-04-30 — substituído
// por `geographyKeys` que mapeia para /admin/geography/*.

export const coverageMapKeys = {
  all: ['coverage-map'] as const,
  filterOptions: () => [...coverageMapKeys.all, 'filter-options'] as const,
  zones: (filters?: Record<string, unknown>) => [...coverageMapKeys.all, 'zones', filters] as const,
  quotations: (filters?: Record<string, unknown>) =>
    [...coverageMapKeys.all, 'quotations', filters] as const,
  consultants: (filters?: Record<string, unknown>) =>
    [...coverageMapKeys.all, 'consultants', filters] as const,
  summary: (filters?: Record<string, unknown>) =>
    [...coverageMapKeys.all, 'summary', filters] as const,
  // Phase Geofences W11 (V3 Coverage Map) — capacidades 2/3/4
  timeline: (filters?: Record<string, unknown>) =>
    [...coverageMapKeys.all, 'timeline', filters] as const,
  gaps: (filters?: Record<string, unknown>) => [...coverageMapKeys.all, 'gaps', filters] as const,
  zoneConversion: (filters?: Record<string, unknown>) =>
    [...coverageMapKeys.all, 'zone-conversion', filters] as const,
} as const;

export const consultantCoverageKeys = {
  all: ['consultant-coverage'] as const,
  detail: (consultantProfileId: string) => ['consultant-coverage', consultantProfileId] as const,
  // Phase Geofences W11 — Cap 5
  suggested: (consultantProfileId: string, q?: Record<string, unknown>) =>
    ['consultant-coverage', 'suggested', consultantProfileId, q] as const,
} as const;

// cepLookupStackKeys removido — funcionalidade absorvida por geographyKeys.resolve.

// W8.5 (plano v4 §6) — Sistema de Link Configurável (admin CRUD).
export const publicQuoteLinkConfigsKeys = {
  all: ['public-quote-link-configs'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...publicQuoteLinkConfigsKeys.all, 'list', filters] as const,
  detail: (id: string) => [...publicQuoteLinkConfigsKeys.all, 'detail', id] as const,
  publicLookup: (slug: string) => [...publicQuoteLinkConfigsKeys.all, 'public', slug] as const,
} as const;

// ESCR-185 — Campanhas/cupons admin (CRUD + workflow SD-1).
// Pagination server-side via { count, rows }; usar `.all` para invalidação broad.
export const campaignsKeys = {
  all: ['campaigns'] as const,
  list: (filters?: Record<string, unknown>) => [...campaignsKeys.all, 'list', filters] as const,
  detail: (id: string) => [...campaignsKeys.all, 'detail', id] as const,
} as const;

// ESCR-EPIC-26 — Novidades/Comunicados (announcements). Feed consultor + CRUD
// admin + surfaces (unread-count / active-popup / active-banner). `.all` invalida
// tudo; socket announcements.count_changed + announcement.published disparam as
// mesmas invalidações. Pagination via { count, rows }.
export const announcementsKeys = {
  all: ['announcements'] as const,
  list: (filters?: Record<string, unknown>) => [...announcementsKeys.all, 'list', filters] as const,
  feed: (filters?: Record<string, unknown>) => [...announcementsKeys.all, 'feed', filters] as const,
  detail: (id: string) => [...announcementsKeys.all, 'detail', id] as const,
  unreadCount: () => [...announcementsKeys.all, 'unread-count'] as const,
  activePopup: () => [...announcementsKeys.all, 'active-popup'] as const,
  activeBanner: () => [...announcementsKeys.all, 'active-banner'] as const,
} as const;

// Proteção FIPE % — protected_value_rules CRUD (ESCR-protected-value-fipe)
export const protectedValueRulesKeys = {
  all: ['protected-value-rules'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...protectedValueRulesKeys.all, 'list', filters] as const,
  detail: (id: string) => [...protectedValueRulesKeys.all, 'detail', id] as const,
  history: (id: string) => [...protectedValueRulesKeys.all, 'history', id] as const,
} as const;

// Phase 01 onboarding-completion (W3 frontend) — keys for the 4 new modules.
export const trainingsKeys = {
  all: ['trainings'] as const,
  list: (filters?: Record<string, unknown>) => [...trainingsKeys.all, 'list', filters] as const,
  detail: (id: string) => [...trainingsKeys.all, 'detail', id] as const,
  config: () => [...trainingsKeys.all, 'config'] as const,
} as const;

export const challengeKeys = {
  all: ['challenge'] as const,
  mine: () => [...challengeKeys.all, 'mine'] as const,
} as const;

export const migrationKeys = {
  all: ['migration'] as const,
  myStatus: () => [...migrationKeys.all, 'my-status'] as const,
  adminQueue: (filters?: Record<string, unknown>) =>
    [...migrationKeys.all, 'admin-queue', filters] as const,
  audit: (id: string) => [...migrationKeys.all, 'audit', id] as const,
} as const;

export const legalTermsKeys = {
  all: ['legal-terms'] as const,
  list: () => [...legalTermsKeys.all, 'list'] as const,
} as const;

// Consulta de carteira via SGA/Hinova — tela /portfolio do consultor.
// Base `/api/v1/sga`. `boletoDownload` resolve link/pix/linha sob demanda.
export const sgaKeys = {
  all: ['sga'] as const,
  vehicleStatus: (plate: string) => [...sgaKeys.all, 'vehicle-status', plate] as const,
  customerVehicles: (cpf: string) => [...sgaKeys.all, 'customer-vehicles', cpf] as const,
  customerStatus: (cpf: string) => [...sgaKeys.all, 'customer-status', cpf] as const,
  events: (plate: string) => [...sgaKeys.all, 'events', plate] as const,
  boletos: (plate: string) => [...sgaKeys.all, 'boletos', plate] as const,
  boletoDownload: (nossoNumero: string) =>
    [...sgaKeys.all, 'boleto-download', nossoNumero] as const,
  overdue: (page: number, limit: number) => [...sgaKeys.all, 'overdue', page, limit] as const,
  overdueByPlate: (plate: string) => [...sgaKeys.all, 'overdue-by-plate', plate] as const,
  overdueByPlatePrefix: () => [...sgaKeys.all, 'overdue-by-plate'] as const,
  /** Prefixo p/ invalidar TODAS as páginas da lista de inadimplentes do consultor. */
  overduePrefix: () => [...sgaKeys.all, 'overdue'] as const,
  overdueAll: (page: number, limit: number, q: string) =>
    [...sgaKeys.all, 'overdue-all', page, limit, q] as const,
  /** Prefixo p/ invalidar TODAS as páginas/filtros da visão admin "todos". */
  overdueAllPrefix: () => [...sgaKeys.all, 'overdue-all'] as const,
} as const;

// Revistoria de reativação por inadimplência — magic link via proposta-sombra.
// Base `/api/v1/reactivations`. Contrato: docs/contracts/ESCR-164-reactivation.md.
export const reactivationKeys = {
  all: ['reactivations'] as const,
  /** Prefixo p/ invalidar todos os mapas de status (todas as combinações de placas). */
  reinspectionStatusAll: () => [...reactivationKeys.all, 'reinspection-status'] as const,
  /** Mapa placa → status. `plates` deve chegar ordenada/dedupada (cache hit estável). */
  reinspectionStatus: (plates: readonly string[]) =>
    [...reactivationKeys.reinspectionStatusAll(), plates] as const,
} as const;

// Troca de titularidade (WAVE 2) — magic-link público do antigo + bucket do analista.
// Contrato: docs/contracts/ESCR-troca-titularidade-wave2.md.
export const ownershipTransferKeys = {
  all: ['ownership-transfer'] as const,
  /** Contexto mascarado da página pública (GET /public/ownership-transfer/:token). */
  publicContext: (token: string) =>
    [...ownershipTransferKeys.all, 'public', 'context', token] as const,
  /** Prefixo p/ invalidar todas as páginas/filtros do bucket "Aguardando titular". */
  queueAll: () => [...ownershipTransferKeys.all, 'queue'] as const,
  queue: (filters?: Record<string, unknown>) =>
    [...ownershipTransferKeys.queueAll(), filters ?? {}] as const,
} as const;

// Central de Chamados de suporte (Escopo A "balcão") — consultor + admin.
// Contrato: docs/contracts/ESCR-271-support-tickets.md. Base `/support/tickets`.
export const supportTicketsKeys = {
  all: ['support-tickets'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...supportTicketsKeys.all, 'list', filters ?? {}] as const,
  detail: (id: string) => [...supportTicketsKeys.all, 'detail', id] as const,
  summary: () => [...supportTicketsKeys.all, 'summary'] as const,
} as const;

// Cotação de FROTA V1 — consultor cria; backoffice acompanha.
// Base `/admin/fleet-quotations`. Contrato: docs/contracts/ESCR-fleet-quotations.md.
export const fleetQuotationsKeys = {
  all: ['fleet-quotations'] as const,
  plateAdvisory: (id: string) =>
    [...fleetQuotationsKeys.all, 'detail', id, 'plate-advisory'] as const,
  defaults: () => [...fleetQuotationsKeys.all, 'defaults'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...fleetQuotationsKeys.all, 'list', filters ?? {}] as const,
  infiniteList: (filters?: Record<string, unknown>) =>
    [...fleetQuotationsKeys.all, 'infinite-list', filters ?? {}] as const,
  detail: (id: string) => [...fleetQuotationsKeys.all, 'detail', id] as const,
  legacyImportOptions: (id: string, itemId: string, planId?: string) =>
    [
      ...fleetQuotationsKeys.all,
      'detail',
      id,
      'legacy-import-options',
      itemId,
      planId ?? '',
    ] as const,
  eligiblePlanGroups: (id: string) =>
    [...fleetQuotationsKeys.all, 'detail', id, 'eligible-plan-groups'] as const,
  customerLookup: (fingerprint: string) =>
    [...fleetQuotationsKeys.all, 'customer-lookup', fingerprint] as const,
  plateResolution: (fingerprint: string) =>
    [...fleetQuotationsKeys.all, 'plate-resolution', fingerprint] as const,
  magicLinks: (id: string) => [...fleetQuotationsKeys.all, 'magic-links', id] as const,
  qualification: (id: string) =>
    [...fleetQuotationsKeys.all, 'detail', id, 'qualification'] as const,
  qualificationHistory: (id: string) =>
    [...fleetQuotationsKeys.all, 'detail', id, 'qualification-history'] as const,
  /** Cobrança única do lote — GET /admin/fleet-quotations/:id/payment (polling 5s). */
  payment: (id: string) => [...fleetQuotationsKeys.all, 'payment', id] as const,
  proposalCoupons: (id: string) =>
    [...fleetQuotationsKeys.all, 'detail', id, 'proposal-coupons'] as const,
  proposalCouponOperation: (id: string) =>
    [...fleetQuotationsKeys.all, 'detail', id, 'proposal-coupons', 'operation'] as const,
} as const;

// Registry tipado de system_settings — tela genérica /admin/settings.
// Base `/admin/settings`. Contrato: docs/contracts/ESCR-settings-admin-screen.md.
export const systemSettingsKeys = {
  all: ['system-settings'] as const,
  registry: () => [...systemSettingsKeys.all, 'registry'] as const,
  detail: (key: string) => [...systemSettingsKeys.all, 'detail', key] as const,
} as const;

// Configuração de assinatura (singleton) — quem contra-assina pela Álamo.
// Contrato: docs/contracts/ESCR-signature-settings.md.
export const signatureSettingsKeys = {
  all: ['signature-settings'] as const,
  detail: () => [...signatureSettingsKeys.all, 'detail'] as const,
} as const;

// Admin de Fornecedores externos (rastreamento + assistência) — provider-agnóstico.
// Base `/admin/suppliers`. Contrato: docs/contracts/ESCR-EPIC-35-suppliers-admin.md.
// Listagem pagina server-side ({ count, rows }); `.all` invalida tudo.
export const suppliersKeys = {
  all: ['suppliers'] as const,
  list: (filters?: Record<string, unknown>) => ['suppliers', 'list', filters ?? {}] as const,
  detail: (id: string) => ['suppliers', 'detail', id] as const,
  credentials: (id: string) => ['suppliers', 'credentials', id] as const,
} as const;

// ESCR-EPIC-35 Fases 1-2 — Config de rastreamento (rules + elegibilidade).
// Contrato: docs/contracts/ESCR-EPIC-35-fase1-config.md.
export const fulfillmentRulesKeys = {
  all: ['fulfillment-rules'] as const,
  list: (filters?: Record<string, unknown>) =>
    ['fulfillment-rules', 'list', filters ?? {}] as const,
  detail: (id: string) => ['fulfillment-rules', 'detail', id] as const,
} as const;

export const supplierEligibilityKeys = {
  all: ['supplier-eligibility'] as const,
  list: (filters?: Record<string, unknown>) =>
    ['supplier-eligibility', 'list', filters ?? {}] as const,
} as const;

// ESCR-EPIC-35 Fase 1 — Dashboard de envios a fornecedores (dispatches).
// Contrato: docs/contracts/ESCR-EPIC-35-fase1-delivery.md.
export const trackingDispatchesKeys = {
  all: ['tracking-dispatches'] as const,
  list: (filters?: Record<string, unknown>) =>
    ['tracking-dispatches', 'list', filters ?? {}] as const,
  detail: (id: string) => ['tracking-dispatches', 'detail', id] as const,
} as const;

// Distribuição de leads de retenção — pool do admin.
// Contrato: docs/contracts/ESCR-retention-lead-distribution.md.
// Toda mutação de membro invalida `.all`: a fatia efetiva de TODOS os membros
// muda quando um deles muda.
export const retentionLeadPoolKeys = {
  all: ['retention-lead-pool'] as const,
  members: (filters?: Record<string, unknown>) =>
    ['retention-lead-pool', 'members', filters ?? {}] as const,
  summary: () => ['retention-lead-pool', 'summary'] as const,
  simulate: (leads: number) => ['retention-lead-pool', 'simulate', leads] as const,
} as const;

export const retentionAssignmentsKeys = {
  all: ['retention-assignments'] as const,
  list: (filters?: Record<string, unknown>) =>
    ['retention-assignments', 'list', filters ?? {}] as const,
} as const;

export const retentionLeadsKeys = {
  all: ['retention-leads'] as const,
  mine: (filters?: Record<string, unknown>) => ['retention-leads', 'mine', filters ?? {}] as const,
} as const;

export const consultantPortfolioTransferKeys = {
  all: ['consultant-portfolio-transfers'] as const,
  list: (filters?: Record<string, unknown>) =>
    ['consultant-portfolio-transfers', 'list', filters ?? {}] as const,
  candidates: (filters?: Record<string, unknown>) =>
    ['consultant-portfolio-transfers', 'candidates', filters ?? {}] as const,
  localItems: (filters?: Record<string, unknown>) =>
    ['consultant-portfolio-transfers', 'local-items', filters ?? {}] as const,
  sgaContracts: (filters?: Record<string, unknown>) =>
    ['consultant-portfolio-transfers', 'sga-contracts', filters ?? {}] as const,
  detail: (id: string) => ['consultant-portfolio-transfers', 'detail', id] as const,
  items: (id: string, filters?: Record<string, unknown>) =>
    ['consultant-portfolio-transfers', 'detail', id, 'items', filters ?? {}] as const,
} as const;
